// Listado cronológico de notas del alumno por clase.
// Cada fila representa una clase del alumno que tiene lección asignada;
// muestra si hay nota (extracto) o si está vacía.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import { listMyTimeline, type NotepadTimelineItem } from '@/services/notepads';
import styles from './MisNotas.module.scss';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${day.charAt(0).toUpperCase() + day.slice(1)} · ${time}`;
}

export function MisNotas() {
  const [items, setItems] = useState<NotepadTimelineItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMyTimeline()
      .then((data) => {
        if (cancelled) return;
        setItems(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const withNotes = useMemo(() => (items ?? []).filter((it) => it.notepad_id !== null), [items]);
  const empty = useMemo(() => (items ?? []).length === 0, [items]);

  return (
    <>
      <Header title="Mis notas" />
      <main className={styles.main}>
        <p className={styles.lead}>
          Tus apuntes están organizados por clase. Pincha en una fila para abrir la lección y editar tu cuaderno.
        </p>

        {loading ? (
          <div className={styles.center}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : empty ? (
          <div className={styles.empty}>
            <Icon name="notebook-pen" size={32} />
            <h3>Aún no tienes clases con lección asignada</h3>
            <p>Cuando entres a una clase con una lección asignada, podrás tomar notas que aparecerán aquí.</p>
          </div>
        ) : (
          <ul className={styles.list}>
            {items!.map((it) => {
              const hasNote = it.notepad_id !== null && (it.notepad_excerpt?.trim().length ?? 0) > 0;
              return (
                <li key={it.booking_id} className={styles.row}>
                  <div className={styles.rowDate}>{formatDate(it.scheduled_at)}</div>
                  <div className={styles.rowBody}>
                    <div className={styles.rowHeader}>
                      <span className={styles.lessonTitle}>{it.lesson_title}</span>
                      <Badge tone={it.booking_type === 'gr' ? 'accent' : 'neutral'}>
                        {it.booking_type === 'gr' ? 'Grupal' : 'Individual'}
                      </Badge>
                    </div>
                    {hasNote ? (
                      <p className={styles.excerpt}>
                        {it.notepad_title ? <strong>{it.notepad_title}: </strong> : null}
                        {it.notepad_excerpt}
                      </p>
                    ) : (
                      <p className={styles.empty1}>Sin notas todavía para esta lección.</p>
                    )}
                  </div>
                  <div className={styles.rowActions}>
                    <Button asChild variant="secondary" size="sm">
                      <Link to={`/lecciones/leccion/${it.lesson_id}?booking=${it.booking_id}`}>
                        <Icon name="notebook-pen" size={14} /> Abrir
                      </Link>
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && !error && !empty && (
          <p className={styles.footnote}>
            {withNotes.length} de {items!.length} clases con notas.
          </p>
        )}
      </main>
    </>
  );
}
